// Costa Rica Hacienda v4.4 electronic-invoice XML parser.
//
// Costa Rica's schema reuses the same leaf tag names (e.g. Numero, Nombre)
// inside both <Emisor> and <Receptor>, so every lookup below is scoped to the
// specific element it belongs to — a flat whole-document tag search (as used
// for El Salvador's DTE in parseElSalvador.js) cannot tell issuer from
// receiver apart here. Do not "simplify" this into a flat search.

// Hacienda v4.4 defines several sibling root document types that share this
// same Emisor/Receptor/ResumenFactura/LineaDetalle shape. Only FacturaElectronica
// has been verified against real files; the others are structurally supported
// per Hacienda's public v4.4 documentation but untested here.
const CR_DOCUMENT_TYPES = {
  FacturaElectronica: 'Factura electrónica',
  TiqueteElectronico: 'Tiquete electrónico',
  NotaCreditoElectronica: 'Nota de crédito electrónica',
  NotaDebitoElectronica: 'Nota de débito electrónica',
  FacturaElectronicaCompra: 'Factura electrónica de compra',
  FacturaElectronicaExportacion: 'Factura electrónica de exportación',
  ReciboElectronicoPago: 'Recibo electrónico de pago',
  // Not independently confirmed as a real v4.4 root type — kept as a
  // harmless no-op possibility (matches nothing if it never appears).
  ReciboElectronicoDonacion: 'Recibo electrónico de donación',
};

function getTextIn(el, tag) {
  const found = el.getElementsByTagNameNS('*', tag)[0];
  return found ? found.textContent.trim() : '';
}

// Cheap detection check used by the schema dispatcher — takes an
// already-parsed Document so a batch of files only gets parsed once each.
export function isCostaRicaXmlDoc(doc) {
  const emisorEl = doc.getElementsByTagNameNS('*', 'Emisor')[0];
  const resumenEl = doc.getElementsByTagNameNS('*', 'ResumenFactura')[0];
  return Boolean(emisorEl && resumenEl);
}

export function parseCostaRicaXml(xmlText, fileName) {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlText, 'application/xml');

    if (doc.querySelector('parsererror')) throw new Error('XML inválido o malformado');

    const emisorEl = doc.getElementsByTagNameNS('*', 'Emisor')[0];
    const receptorEl = doc.getElementsByTagNameNS('*', 'Receptor')[0];
    const resumenEl = doc.getElementsByTagNameNS('*', 'ResumenFactura')[0];

    if (!emisorEl || !resumenEl) throw new Error('No es un comprobante electrónico válido de Costa Rica');

    const rootName = doc.documentElement.localName || doc.documentElement.tagName;
    const documentType = CR_DOCUMENT_TYPES[rootName] ? rootName : 'FacturaElectronica';
    const documentTypeLabel = CR_DOCUMENT_TYPES[rootName] || CR_DOCUMENT_TYPES.FacturaElectronica;

    const lineas = Array.from(doc.getElementsByTagNameNS('*', 'LineaDetalle'));
    const items = lineas
      .map(linea => ({
        numLinea: parseInt(getTextIn(linea, 'NumeroLinea'), 10) || 0,
        cantidad: parseFloat(getTextIn(linea, 'Cantidad')) || 0,
        unidad: getTextIn(linea, 'UnidadMedida'),
        detalle: getTextIn(linea, 'Detalle'),
        precioUnitario: parseFloat(getTextIn(linea, 'PrecioUnitario')) || 0,
        montoTotalLinea: parseFloat(getTextIn(linea, 'MontoTotalLinea')) || 0,
      }))
      .sort((a, b) => a.numLinea - b.numLinea);

    return {
      ok: true,
      data: {
        fileName,
        documentType,
        documentTypeLabel,
        clave: getTextIn(doc, 'Clave'),
        numeroConsecutivo: getTextIn(doc, 'NumeroConsecutivo'),
        fecha: getTextIn(doc, 'FechaEmision').slice(0, 10),
        emisorNombre: getTextIn(emisorEl, 'Nombre'),
        emisorId: getTextIn(emisorEl, 'Numero'),
        receptorNombre: receptorEl ? getTextIn(receptorEl, 'Nombre') : '',
        moneda: getTextIn(resumenEl, 'CodigoMoneda') || 'CRC',
        totalGravado: parseFloat(getTextIn(resumenEl, 'TotalGravado')) || 0,
        totalImpuesto: parseFloat(getTextIn(resumenEl, 'TotalImpuesto')) || 0,
        totalComprobante: parseFloat(getTextIn(resumenEl, 'TotalComprobante')) || 0,
        items,
      },
    };
  } catch (err) {
    return { ok: false, error: err.message, fileName };
  }
}

// Dedup by clave (re-importing the same invoice is a no-op), newest first.
export function mergeCostaRicaInvoices(existing, incoming) {
  const seen = new Set(existing.map(f => f.clave));
  const unique = incoming.filter(f => !seen.has(f.clave));
  return [...existing, ...unique].sort((a, b) => b.fecha.localeCompare(a.fecha));
}
